import { Link } from '@tanstack/react-router';
import { PageShell, Section } from '~/components/layout';
import { ArticleReadingLayout } from '~/components/sections/ArticleReadingLayout';
import { CtaBanner } from '~/components/sections/CtaBanner';
import { Block, SectionHeading } from '~/components/sections/LearnArticleDetail';
import { Grid } from '~/components/ui';
import type { ArticleBlock } from '~/content/learn';
import type { NewsArticle } from '~/content/news';
import { cn } from '~/lib/cn';
import { sectionGap } from '~/lib/content-rhythm';

interface NewsSegment {
  heading: Extract<ArticleBlock, { type: 'heading' }> | null;
  blocks: Exclude<ArticleBlock, { type: 'heading' }>[];
}

function toSegments(body: ArticleBlock[]): NewsSegment[] {
  const segments: NewsSegment[] = [];
  let current: NewsSegment = { heading: null, blocks: [] };
  for (const block of body) {
    if (block.type === 'heading') {
      segments.push(current);
      current = { heading: block, blocks: [] };
    } else {
      current.blocks.push(block);
    }
  }
  segments.push(current);
  return segments;
}

/**
 * Press release detail. Same reading column and typography as the Learn article
 * page, without the quick-nav sidebar or author byline.
 */
export function NewsArticleDetail({ article }: { article: NewsArticle }) {
  const [intro, ...sections] = toSegments(article.body);

  return (
    <PageShell showFooterLegalDisclosure={false}>
      <Section className="pt-10 pb-36 md:pt-14 md:pb-44">
        <Grid>
          <ArticleReadingLayout>
            <Link
              to="/news"
              className="group inline-flex items-center gap-2 text-body-s font-medium text-red-500 transition-opacity duration-150 ease-nav hover:opacity-70">
              <svg viewBox="0 0 24 24" fill="none" className="size-5" aria-hidden="true">
                <path
                  d="M7.68115 18.5L2.86084 12.5674L2.3999 12L2.86084 11.4326L7.68115 5.5L10.0005 5.5L4.71924 12L10.0005 18.5L7.68115 18.5Z"
                  fill="currentColor"
                />
                <path d="M4 12H21.5" stroke="currentColor" strokeWidth={1.8} />
              </svg>
              <span className="link-underline-lr bg-origin-content">All news</span>
            </Link>

            <article>
              <header className="mt-7">
                <time
                  dateTime={article.publishedAt}
                  className="text-body-s font-medium text-grey-500">
                  {article.date}
                </time>
                <h1 className="mt-4 text-h3 leading-[1.08] font-semibold tracking-[-0.035em] text-balance md:text-[42px]">
                  {article.title}
                </h1>
                {article.subtitle ? (
                  <p className="mt-5 text-h5 font-medium tracking-[-0.02em] text-grey-700">
                    {article.subtitle}
                  </p>
                ) : null}
              </header>

              {intro.blocks.length > 0 && (
                <div className="mt-9 space-y-5">
                  {intro.blocks.map((block, index) => (
                    <Block key={index} block={block} />
                  ))}
                </div>
              )}

              {sections.length > 0 && (
                <div className="mt-18">
                  {sections.map((segment, index) => {
                    const level = segment.heading?.level ?? 2;
                    return (
                      <section key={index} className={cn(index > 0 && sectionGap(level))}>
                        <SectionHeading level={level}>{segment.heading?.text ?? ''}</SectionHeading>
                        {segment.blocks.length > 0 && (
                          <div className={cn('space-y-5', level >= 3 ? 'mt-5' : 'mt-7')}>
                            {segment.blocks.map((block, blockIndex) => (
                              <Block key={blockIndex} block={block} />
                            ))}
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>
              )}
            </article>
          </ArticleReadingLayout>
        </Grid>
      </Section>
      <CtaBanner />
    </PageShell>
  );
}
